'use client';

import { API_ROUTES } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useLongPoll } from '@shared/hooks';
import { createContext, useCallback, useRef, useState } from 'react';
import { useGlobalDialogsEnabled } from '../hooks/useGlobalDialogsEnabled';
import type { AffordabilityCheckDialogResponse } from '../types';

type GlobalDialogPollingContextType = {
  data: AffordabilityCheckDialogResponse | null | undefined;
  registerDialog: (dialogId: string, isOpen: boolean) => void;
};

const GlobalDialogPollingContext = createContext<GlobalDialogPollingContextType | undefined>(undefined);

/**
 * Provider component that manages the long polling for global dialogs.
 *
 * - Polls the PROMPT_DIALOG endpoint once for all dialogs
 * - Only polls when user is authenticated (has session)
 * - Tracks which dialogs are currently open
 * - Pauses polling when any dialog is open to prevent race conditions
 * - Uses default useLongPoll interval (25s per Core Domain requirement)
 *
 * This ensures we only make one polling request instead of multiple duplicate requests.
 */
function GlobalDialogPollingProvider({ children }: { children: React.ReactNode }) {
  const shouldEnablePolling = useGlobalDialogsEnabled();
  const openDialogsRef = useRef<Set<string>>(new Set());
  const [hasOpenDialogs, setHasOpenDialogs] = useState(false);

  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';

  const { data } = useLongPoll<AffordabilityCheckDialogResponse>(API_ROUTES.PLAYER_KYC.PROMPT_DIALOG, {
    enabled: !hasOpenDialogs && shouldEnablePolling && isLoggedIn,
    setDataTo: { id: null, messageType: 'NO_DIALOG_TO_SHOW' },
    credentials: true,
  });

  const registerDialog = useCallback((dialogId: string, isOpen: boolean) => {
    if (isOpen) {
      openDialogsRef.current.add(dialogId);
    } else {
      openDialogsRef.current.delete(dialogId);
    }
    setHasOpenDialogs(openDialogsRef.current.size > 0);
  }, []);

  return <GlobalDialogPollingContext.Provider value={{ data, registerDialog }}>{isLoggedIn ? children : null}</GlobalDialogPollingContext.Provider>;
}

export { GlobalDialogPollingContext, GlobalDialogPollingProvider };
export type { GlobalDialogPollingContextType };
